/**
 * Top-level routes for the agent-governance-demo SPA.
 *
 * Consumer routes: `/catalog`, `/consumer/agents/:id`, `/invoke/:id`.
 * Admin routes:    `/admin/*` (incl. `/admin/agents/:id`).
 * Public routes:   `/about`, `/about/design`, `/examples`.
 * Auth:            `/auth/callback` handles the Cognito redirect.
 *
 * Role enforcement happens in <RouteGuard/>. A missing Admin claim on
 * an /admin route renders <AuthorizationError/> (Requirement 2.5).
 *
 * Routes new in tasks 18.2 / 18.3:
 *   - `/admin/agents/:id`    — Admin variant of the Capability_Inventory
 *     page (`AgentInventoryPage`). Distinct from
 *     `/admin/agents/:id/edit`, which is the existing Admin Edit Agent
 *     surface and is preserved as-is.
 *   - `/consumer/agents/:id` — Consumer variant of the Capability_Inventory
 *     page (`AgentInventoryPage`). Same component, role-aware data
 *     fetching is handled inside `useInventoryData`.
 *   - `/about/design`        — Public Design_Choices_Page
 *     (`DesignChoicesPage`). No `RouteGuard` — Requirement 10.1
 *     requires logged-out visitors to reach it.
 *   - `/agents/:id`          — Legacy deep-link path. Now a SPA redirect
 *     to `/consumer/agents/:id` (replace navigation, so it doesn't
 *     leave a back-stack entry that bounces the user). Resolves design
 *     open question #1 by keeping pre-existing demo links working
 *     instead of returning a 404.
 *
 * Code splitting (post-migration cleanup):
 * Every page route below is loaded via `React.lazy(() => import(...))`
 * so each page becomes its own chunk. This shrinks the initial
 * payload from one ~1.1MB bundle to a small shell + per-page
 * chunks. The shell components (`CloudscapeShell`, `RouteGuard`,
 * `AuthorizationError`) and the auth callback (`AuthCallback`,
 * which is tiny and hits the network anyway) stay in the main
 * bundle — lazy-loading them would add a network round-trip with
 * no payload-size win. The `LegacyAgentDetailRedirect` helper is a
 * one-line redirect and is kept inline for the same reason.
 *
 * Vite supports the same magic-comment chunk-name syntax as webpack,
 * so each lazy import carries `/* webpackChunkName: "..." *\/` to
 * give each chunk a stable, debuggable filename.
 */

import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";

import Box from "@cloudscape-design/components/box";
import Spinner from "@cloudscape-design/components/spinner";

import { CloudscapeShell } from "./components/CloudscapeShell";
import { RouteGuard } from "./components/RouteGuard";
import { AuthorizationError } from "./components/AuthorizationError";
import { useStageWatcher } from "./hooks/useStageWatcher";
import { AuthCallback } from "./pages/AuthCallback";

// Lazy-loaded page modules. Each `.then(...)` adapts the named export
// to the default-export shape that `React.lazy` requires.
const Home = lazy(() =>
  import(/* webpackChunkName: "home" */ "./pages/Home").then((m) => ({
    default: m.Home,
  })),
);
const ConsumerCatalog = lazy(() =>
  import(
    /* webpackChunkName: "consumer-catalog" */ "./pages/ConsumerCatalog"
  ).then((m) => ({ default: m.ConsumerCatalog })),
);
const ConsumerInvoke = lazy(() =>
  import(
    /* webpackChunkName: "consumer-invoke" */ "./pages/ConsumerInvoke"
  ).then((m) => ({ default: m.ConsumerInvoke })),
);
const AgentInventoryPage = lazy(() =>
  import(
    /* webpackChunkName: "agent-inventory" */ "./pages/agent-inventory"
  ).then((m) => ({ default: m.AgentInventoryPage })),
);
const AdminCatalog = lazy(() =>
  import(/* webpackChunkName: "admin-catalog" */ "./pages/AdminCatalog").then(
    (m) => ({ default: m.AdminCatalog }),
  ),
);
const AdminPending = lazy(() =>
  import(/* webpackChunkName: "admin-pending" */ "./pages/AdminPending").then(
    (m) => ({ default: m.AdminPending }),
  ),
);
const AdminSubmit = lazy(() =>
  import(/* webpackChunkName: "admin-submit" */ "./pages/AdminSubmit").then(
    (m) => ({ default: m.AdminSubmit }),
  ),
);
const AdminEditAgent = lazy(() =>
  import(
    /* webpackChunkName: "admin-edit-agent" */ "./pages/AdminEditAgent"
  ).then((m) => ({ default: m.AdminEditAgent })),
);
const AdminSkills = lazy(() =>
  import(/* webpackChunkName: "admin-skills" */ "./pages/AdminSkills").then(
    (m) => ({ default: m.AdminSkills }),
  ),
);
const SkillDetailPage = lazy(() =>
  import(
    /* webpackChunkName: "skill-detail" */ "./pages/skill-detail"
  ).then((m) => ({ default: m.SkillDetailPage })),
);
const SkillCatalog = lazy(() =>
  import(
    /* webpackChunkName: "skill-catalog" */ "./pages/SkillCatalog"
  ).then((m) => ({ default: m.SkillCatalog })),
);
const AdminPolicy = lazy(() =>
  import(
    /* webpackChunkName: "admin-policy" */ "./pages/admin-policy"
  ).then((m) => ({ default: m.AdminPolicy })),
);
const AdminObservability = lazy(() =>
  import(
    /* webpackChunkName: "admin-observability" */ "./pages/admin-observability"
  ).then((m) => ({ default: m.AdminObservability })),
);
const AboutGovernance = lazy(() =>
  import(
    /* webpackChunkName: "about-governance" */ "./pages/AboutGovernance"
  ).then((m) => ({ default: m.AboutGovernance })),
);
const DesignChoicesPage = lazy(() =>
  import(
    /* webpackChunkName: "about-design" */ "./pages/about-design"
  ).then((m) => ({ default: m.DesignChoicesPage })),
);
const Examples = lazy(() =>
  import(/* webpackChunkName: "examples" */ "./pages/Examples").then((m) => ({
    default: m.Examples,
  })),
);
// `AuthorizationError` is also reached statically via `RouteGuard`
// (which renders it inline on a missing-role failure), so it lives
// in the main bundle anyway. The `/unauthorized` route therefore
// uses the direct import — lazy-loading it would just add a useless
// extra chunk that re-exports a module we already loaded.

/**
 * Suspense fallback rendered while a lazy page chunk is being
 * fetched. Cloudscape Spinner inside a centred Box — the shell
 * (RoleNav, AppLayout) is already mounted around it so users see a
 * stable frame with a clear loading affordance.
 */
function PageFallback() {
  return (
    <Box textAlign="center" padding="xl">
      <Spinner size="large" /> Loading…
    </Box>
  );
}

/**
 * Legacy `/agents/:id` → `/consumer/agents/:id` redirect.
 *
 * Implemented as a tiny component (not a one-line `<Navigate to="..."
 * />`) because we need to read the dynamic `:id` segment via
 * `useParams` and forward it. Uses `replace` so the redirect step
 * does not pollute browser history (the user can click Back from
 * `/consumer/agents/:id` and land on whatever page they came from,
 * not a route that immediately bounces them again).
 */
function LegacyAgentDetailRedirect() {
  const { id = "" } = useParams();
  return (
    <Navigate to={`/consumer/agents/${encodeURIComponent(id)}`} replace />
  );
}

export function App() {
  // Mount cross-Stage cache flush watcher at the SPA root so it runs
  // for every page load before any other hook or render logic
  // (Requirement 13.6, design.md §"9.2 SPA — cross-Stage cache clear",
  // task 19.4).
  useStageWatcher();

  return (
    <Routes>
      <Route element={<CloudscapeShell />}>
        <Route
          index
          element={
            <Suspense fallback={<PageFallback />}>
              <Home />
            </Suspense>
          }
        />
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Consumer routes */}
        <Route
          path="/catalog"
          element={
            <RouteGuard requiredRole="Consumer">
              <Suspense fallback={<PageFallback />}>
                <ConsumerCatalog />
              </Suspense>
            </RouteGuard>
          }
        />
        <Route
          path="/consumer/agents/:id"
          element={
            <RouteGuard requiredRole="Consumer">
              <Suspense fallback={<PageFallback />}>
                <AgentInventoryPage />
              </Suspense>
            </RouteGuard>
          }
        />
        {/* Legacy SPA redirect: pre-existing demo deep links use
            /agents/:id. Forward them to the new role-scoped route
            (task 18.3). */}
        <Route path="/agents/:id" element={<LegacyAgentDetailRedirect />} />
        <Route
          path="/invoke/:id"
          element={
            <RouteGuard requiredRole="Consumer">
              <Suspense fallback={<PageFallback />}>
                <ConsumerInvoke />
              </Suspense>
            </RouteGuard>
          }
        />
        {/* Consumer Skill_Catalog (browse + integrated search). Folds
            in the former standalone find-a-skill surface. */}
        <Route
          path="/skills"
          element={
            <RouteGuard requiredRole="Consumer">
              <Suspense fallback={<PageFallback />}>
                <SkillCatalog />
              </Suspense>
            </RouteGuard>
          }
        />
        {/* Legacy find-a-skill path → redirect to the catalog (search
            is now integrated there). */}
        <Route path="/skills/find" element={<Navigate to="/skills" replace />} />

        {/* Admin routes */}
        <Route
          path="/admin"
          element={<Navigate to="/admin/catalog" replace />}
        />
        <Route
          path="/admin/catalog"
          element={
            <RouteGuard requiredRole="Admin">
              <Suspense fallback={<PageFallback />}>
                <AdminCatalog />
              </Suspense>
            </RouteGuard>
          }
        />
        <Route
          path="/admin/agents/:id"
          element={
            <RouteGuard requiredRole="Admin">
              <Suspense fallback={<PageFallback />}>
                <AgentInventoryPage />
              </Suspense>
            </RouteGuard>
          }
        />
        <Route
          path="/admin/agents/:id/edit"
          element={
            <RouteGuard requiredRole="Admin">
              <Suspense fallback={<PageFallback />}>
                <AdminEditAgent />
              </Suspense>
            </RouteGuard>
          }
        />
        <Route
          path="/admin/skills"
          element={
            <RouteGuard requiredRole="Admin">
              <Suspense fallback={<PageFallback />}>
                <AdminSkills />
              </Suspense>
            </RouteGuard>
          }
        />
        <Route
          path="/admin/skills/:id"
          element={
            <RouteGuard requiredRole="Admin">
              <Suspense fallback={<PageFallback />}>
                <SkillDetailPage />
              </Suspense>
            </RouteGuard>
          }
        />
        <Route
          path="/admin/pending"
          element={
            <RouteGuard requiredRole="Admin">
              <Suspense fallback={<PageFallback />}>
                <AdminPending />
              </Suspense>
            </RouteGuard>
          }
        />
        <Route
          path="/admin/submit"
          element={
            <RouteGuard requiredRole="Admin">
              <Suspense fallback={<PageFallback />}>
                <AdminSubmit />
              </Suspense>
            </RouteGuard>
          }
        />
        <Route
          path="/admin/policy"
          element={
            <RouteGuard requiredRole="Admin">
              <Suspense fallback={<PageFallback />}>
                <AdminPolicy />
              </Suspense>
            </RouteGuard>
          }
        />
        <Route
          path="/admin/observability"
          element={
            <RouteGuard requiredRole="Admin">
              <Suspense fallback={<PageFallback />}>
                <AdminObservability />
              </Suspense>
            </RouteGuard>
          }
        />
        <Route
          path="/admin/about"
          element={
            <RouteGuard requiredRole="Admin">
              <Suspense fallback={<PageFallback />}>
                <AboutGovernance />
              </Suspense>
            </RouteGuard>
          }
        />
        <Route
          path="/about"
          element={
            <Suspense fallback={<PageFallback />}>
              <AboutGovernance />
            </Suspense>
          }
        />
        {/* Public Design_Choices_Page — no RouteGuard so logged-out
            visitors can reach it (Requirement 10.1, task 18.2). */}
        <Route
          path="/about/design"
          element={
            <Suspense fallback={<PageFallback />}>
              <DesignChoicesPage />
            </Suspense>
          }
        />

        {/* Examples page — static public docs, no RouteGuard (R7.1, R7.8). */}
        <Route
          path="/examples"
          element={
            <Suspense fallback={<PageFallback />}>
              <Examples />
            </Suspense>
          }
        />

        <Route
          path="/unauthorized"
          element={<AuthorizationError />}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
